import { Col, Descriptions, Row, Tag } from 'antd';
import { Button, Card, Divider, Table } from 'pink-lava-ui';
import { type BookingDraft } from '@dc-trans/data-layer';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import ExpandableContainer from './ExpandableContainer';
import { useBookingOnDemandInfoById } from '@/queries';
import { getStatusAttr } from './utils';
import { useLocation, useNavigate } from 'react-router-dom';

interface ShipmentData {
  tms_ref_number: string;
  origin_name: string;
  destination_name: string;
}

interface OnDemandCardProps {
  booking: BookingDraft;
  onConfirmClick: () => void;
}

const CountdownTimer = ({ targetDate, onExpired }: { targetDate: string; onExpired?: () => void }) => {
  const [timeLeft, setTimeLeft] = useState<string>('');

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = dayjs();
      const target = dayjs(targetDate);
      const diff = target.diff(now);

      if (diff <= 0) {
        setTimeLeft('00:00:00');
        onExpired?.();
        return true;
      }

      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);

      const format = (n: number) => n.toString().padStart(2, '0');
      setTimeLeft(`${format(h)}:${format(m)}:${format(s)}`);
      return false;
    };

    if (!calculateTimeLeft()) {
      const timer = setInterval(() => {
        if (calculateTimeLeft()) {
          clearInterval(timer);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [targetDate, onExpired]);

  return (
    <Tag className="bg-red-500! text-white! font-nunito!" bordered={false} color="red">
      {timeLeft}
    </Tag>
  );
};

const OnDemandCard = ({ booking, onConfirmClick }: OnDemandCardProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isMobile, isLgAndUp, isXlAndUp, isMdAndUp } = useBreakpoint();
  const [onInfoExpanded, setOnInfoExpanded] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const bookingInfoQuery = useBookingOnDemandInfoById(booking.ulid, onInfoExpanded);

  const bookingInfo = bookingInfoQuery.data?.data;

  const isShowConfirmBooking = booking.status === 'new';

  const onViewBookingClick = () => {
    navigate(`/booking-list?tms_booking=${booking.tms_booking}`, {
      state: {
        previousPath: pathname,
      },
    });
  };
  
  const maskBookingNumber = (bookingNumber?: string) => {
    if (!bookingNumber) return '-';

    if (/^\d{9}$/.test(bookingNumber)) {
      return `${bookingNumber.slice(0, 3)}XXXXXX`;
     }
      return bookingNumber;
    };

   const shouldMaskOnDemandBookingNumber = (status?: string) => {
     return status === 'new';
   };

   const displayBookingNumber = shouldMaskOnDemandBookingNumber(booking.status)
  ? maskBookingNumber(booking.tms_booking)
  : booking.tms_booking;

  return (
    <Card rounded="large" size={isMobile ? 'small' : 'default'}>
      <Row gutter={[16, 16]} className="flex items-end justify-end">
        <Col span={24}>
          <div className="flex flex-row items-center">
            <div className="text-lg w-full flex-1 items-center flex flex-row gap-2">
              {displayBookingNumber}
              {isShowConfirmBooking && booking.max_confirmed_at && (
                <CountdownTimer targetDate={booking.max_confirmed_at} onExpired={() => setIsExpired(true)} />
              )}
            </div>
            <Tag color={getStatusAttr(booking.status).color} bordered={false} className={`text-sm! mr-0! font-nunito!`}>
              {getStatusAttr(booking.status).label}
            </Tag>
          </div>
        </Col>
        <Col flex={24} className="flex flex-row gap-6 items-end">
          <Descriptions
            colon={false}
            size="small"
            column={isXlAndUp ? 3 : isMdAndUp ? 2 : 1}
            layout={isMdAndUp ? 'vertical' : 'horizontal'}
            classNames={{
              root: 'flex-1',
              label: 'font-nunito text-gray-400 text-xs',
              content: 'font-nunito flex-1 text-xs text-right justify-end md:text-left md:justify-start',
            }}
            items={[
              {
                label: 'Dibuat Pada',
                children: booking.created_at ? dayjs(booking.created_at).format('DD MMMM YYYY HH:mm:ss') : '-',
              },
              {
                label: 'Req. Plant Delivery Date',
                children: booking.req_plant_delivery_date
                  ? dayjs(booking.req_plant_delivery_date).format('DD MMMM YYYY')
                  : '-',
              },
              {
                label: 'Req. Shipping Window',
                children: booking.req_ship_window
                  ? `${booking.req_ship_window?.incoming} - ${booking.req_ship_window?.outgoing}`
                  : '-',
              },
              {
                label: 'Req. Jenis Kendaraan',
                children: booking.req_vehicle_type ? booking.req_vehicle_type.name : '-',
              },
              {
                label: 'Batas Waktu Konfirmasi',
                children: dayjs(booking.max_confirmed_at).format('DD MMMM YYYY HH:mm:ss'),
              },
            ]}
          />
          {isShowConfirmBooking && isLgAndUp && (
            <Button shape="round" size="small" onClick={onConfirmClick} disabled={isExpired}>
              Konfirmasi
            </Button>
          )}
          {booking.status === 'completed' && isLgAndUp && (
            <Button shape="round" size="small" type="secondary" onClick={onViewBookingClick} disabled={isExpired}>
              Daftar Booking
            </Button>
          )}
        </Col>
      </Row>
      <Divider className="my-4!" />
      {isShowConfirmBooking && (
        <div className="flex flex-row items-center pb-4 justify-end lg:hidden">
          <Button shape="round" size="small" block onClick={onConfirmClick} disabled={isExpired}>
            Konfirmasi
          </Button>
        </div>
      )}
      {booking.status === 'completed' && (
        <div className="flex flex-row items-center pb-4 justify-end lg:hidden">
          <Button shape="round" size="small" block type="secondary" onClick={onViewBookingClick} disabled={isExpired}>
            Daftar Booking
          </Button>
        </div>
      )}
      <div className="flex justify-between">
        <ExpandableContainer
          minimizedHeight={0}
          buttonText="Tampilkan Detail"
          className="w-full"
          buttonPlacement="right"
          onExpandedChange={setOnInfoExpanded}
        >
          <div className="flex flex-col gap-4">
            <Table
              size="small"
              dataSource={bookingInfo?.logs || []}
              pagination={false}
              scroll={{ x: 480 }}
              columns={[
                {
                  title: 'Tanggal & Waktu',
                  dataIndex: 'created_at',
                  key: 'created_at',
                  width: 140,
                  render: text => (text ? dayjs(text).format('DD MMMM YYYY HH:mm:ss') : '-'),
                },
                {
                  title: 'Status',
                  dataIndex: 'action',
                  render: (value: string) => (
                    <Tag color={getStatusAttr(value).color} bordered={false} className={`text-sm! mr-0! font-nunito!`}>
                      {getStatusAttr(value).label}
                    </Tag>
                  ),
                },
                {
                  title: 'Change Log',
                  dataIndex: 'notes',
                },
              ]}
              rowKey="ulid"
            />
            <Table
              size="small"
              dataSource={bookingInfo?.routes || []}
              pagination={false}
              columns={[
                {
                  title: 'Asal Pengiriman',
                  dataIndex: 'origin_name',
                  key: 'origin_name',
                },
                {
                  title: 'Tujuan Pengiriman',
                  dataIndex: 'destination_name',
                  key: 'destination_name',
                },
              ]}
              rowKey={record => `${record.origin_ulid}-${record.destination_ulid}`}
            />
          </div>
        </ExpandableContainer>
      </div>
    </Card>
  );
};

export default OnDemandCard;
export type { OnDemandCardProps, ShipmentData };
